import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import TextField from '../TextField';
import Button from '../../../misc/Button';

import { chartColors } from '../../../../constants/UtilityColors';
import { getBackgroundContrastedColor } from '../../../../lib/colors';
import FormStylesOptions from '../../../../constants/FormStylesOptions';

import ThemeIcons from '../../../../theme/Icons';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
  position: relative;
  border: ${(props) => (props.border && props.borderWidth ? `${props.border}` : '1px solid #E2E3E7')};
  background-color: ${(props) => (props.backgroundColor ? `${props.backgroundColor}` : '#FFFFFF')};
  border-radius: ${(props) => (props.borderRadius ? `${props.borderRadius}px` : '5px')};
`;

const StyledHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const StyledHeaderInputs = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  flex-direction: row;
`;

const StyledInputWrapper = styled.div`
  margin-left: 4px;
`;

const StyledColorWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${(props) => chartColors[props.index]};
  background-color: ${(props) => getBackgroundContrastedColor(chartColors[props.index])};
  border-radius: ${(props) => props.borderRadius}px;
  height: 25px;
`;

const StyledColor = styled.div`
  width: 10px;
  height: 10px;
  background-color: ${(props) => chartColors[props.index]};
  border: 1px solid ${(props) => chartColors[props.index]};
  border-radius: 100px;
  margin: 8px;
`;

const StyledTable = styled.table`
  
  width: 100%;
  font-size:${(props) => props.fontSize};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  color: ${(props) => props.fontColor};
  background-color: ${(props) => props.backgroundColor};

`;

const StyledTableHeader = styled.th`
  text-align: left;
  font-size: ${(props) => props.fontSize}px;
  color: ${(props) => props.fontColor};
`;

const StyledTableBody = styled.tbody`
`;

const StyledTableRow = styled.tr`
`;

const StyledTableData = styled.td`
 
`;

const ChartFieldLine = ({
  id,
  lineId,
  lineIndex,
  yTitle,
  xTitle,
  title,
  description,
  points = [],
  disabled,

  primaryColor,

  //Field style
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderRadius,
  borderWidth,
  backgroundColor,
  padding,

  //Action handlers
  onDelete,
  onChange,
  onPointCreate,
  onPointChange,
  onPointDelete,
}) => {

  const titleStyles = {
    titleFontSize: fontSize,
    titleFontStyle: fontStyle,
    titleFontWeight: 'bold',
    titleTextTransform: textTransform,
  };

  const fieldStyles = {
    primaryColor,
    fontSize,
    fontColor,
    fontStyle,
    fontWeight,
    textAlign,
    textTransform,
    textDecoration,
    textOverflow,
    borderColor,
    borderRadius,
    borderWidth,
    backgroundColor,
    padding,
  };

  /**
   * We join width and color to a single border style because chrome displays
   * the border as a two tone color if you use border-color and border-width
   * indvidually.
   */
  const border = `${borderWidth}px solid ${borderColor}`;

  const pointNodes = points.map((point, pointIndex) => (
    <StyledTableRow key={pointIndex}>
      <StyledTableData {...{ border, borderRadius, backgroundColor, borderWidth }}>
        <TextField
          type="text"
          {...fieldStyles}
          disabled={disabled}
          value={point.label}
          placeholder="Label"
          onChange={(f, i, p, value) => onPointChange(lineId, point._id, 'label', value)}
          borderWidth={borderWidth}
        />
      </StyledTableData>
      <StyledTableData {...{ border, borderRadius, backgroundColor, borderWidth }}>
        <TextField
          {...fieldStyles}
          disabled={disabled}
          placeholder={yTitle}
          value={typeof point.y === 'number' ? point.y : ''}
          type="number"
          textOverflow={FormStylesOptions.textOverflowEllipsis}
          onChange={(f, i, p, value) => onPointChange(lineId, point._id, 'y', value ? parseFloat(value) : 0)}
        />
      </StyledTableData>
      <StyledTableData {...{ border, borderRadius, backgroundColor, borderWidth }}>
        <TextField
          {...fieldStyles}
          disabled={disabled}
          type="number"
          placeholder={xTitle}
          value={typeof point.x === 'number' ? point.x : ''}
          textOverflow={FormStylesOptions.textOverflowEllipsis}
          onChange={(f, i, p, value) => onPointChange(lineId, point._id, 'x', value ? parseFloat(value) : 0)}
        />
      </StyledTableData>
      {disabled ? null : (
        <StyledTableData {...{ border, borderRadius, backgroundColor, borderWidth }}>
          <Button
            styles={{ border: '1px solid transparent', padding: '4px' }}
            danger
            icon={ThemeIcons.settings.delete}
            onClick={() => onPointDelete(lineId, point._id, pointIndex)}
          />
        </StyledTableData>
      )}
    </StyledTableRow>
  ));

  return (
    <StyledWrapper {...{ border, borderRadius, backgroundColor, borderWidth }}>
      <StyledHeaderWrapper>
        <StyledColorWrapper {...{ borderRadius, index: lineIndex }}>
          <StyledColor index={lineIndex} />
        </StyledColorWrapper>
        <StyledHeaderInputs>
          <StyledInputWrapper>
            <TextField
              type="text"
              fieldId={lineId}
              id={`${id}-title`}
              disabled={disabled}
              value={title}
              placeholder="Title"
              {...titleStyles}
              {...fieldStyles}
              onChange={(f, i, p, value) => onChange(lineId, 'title', value)}
            />
          </StyledInputWrapper>
          <StyledInputWrapper>
            <TextField
              type="text"
              fieldId={lineId}
              id={`${id}-description`}
              disabled={disabled}
              placeholder="Details"
              value={description}
              {...titleStyles}
              {...fieldStyles}
              onChange={(f, i, p, value) => onChange(lineId, 'description', value)}
            />
          </StyledInputWrapper>
        </StyledHeaderInputs>
        {disabled ? null : (
          <>
            <Button
              styles={{ marginRight: '4px', padding: '6px 12px' }}
              text="Point"
              icon={ThemeIcons.misc.add}
              onClick={() => onPointCreate(lineId, lineIndex)}
            />
            <Button
              danger
              styles={{ padding: '6px 12px' }}
              icon={ThemeIcons.settings.delete}
              onClick={() => onDelete(lineId, lineIndex)}
            />
          </>
        )}
      </StyledHeaderWrapper>
      <StyledTable {...{ border, borderRadius, backgroundColor, borderWidth }}>
        <StyledTableBody>
          <StyledTableRow>
            <StyledTableHeader
              {...{ border, borderRadius, backgroundColor, borderWidth }}
              {...fieldStyles}
            >
              Label
            </StyledTableHeader>
            <StyledTableHeader
              {...{ border, borderRadius, backgroundColor, borderWidth }}
              {...fieldStyles}
            >
              {yTitle}
            </StyledTableHeader>
            <StyledTableHeader
              {...{ border, borderRadius, backgroundColor, borderWidth }}
              {...fieldStyles}
            >
              {xTitle}
            </StyledTableHeader>
            {disabled ? null : (
              <StyledTableHeader
                {...{ border, borderRadius, backgroundColor, borderWidth }}
                {...fieldStyles}
                style={{ width: '12px' }}
              >
                {' '}
              </StyledTableHeader>
            )}
          </StyledTableRow>
          {pointNodes}
        </StyledTableBody>
      </StyledTable>
    </StyledWrapper>
  );

};

ChartFieldLine.propTypes = {
  id: PropTypes.string,
  lineId: PropTypes.string,
  lineIndex: PropTypes.number,
  disabled: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
  points: PropTypes.array, //[{_id: String, y: Number, x: Number}, ...]
  yTitle: PropTypes.string,
  xTitle: PropTypes.string,

  //Field style
  fontSize: PropTypes.number,
  fontColor: PropTypes.string,
  fontStyle: PropTypes.string,
  fontWeight: PropTypes.string,
  textAlign: PropTypes.string,
  textTransform: PropTypes.string,
  textDecoration: PropTypes.string,
  textOverflow: PropTypes.string,
  borderColor: PropTypes.string,
  borderRadius: PropTypes.number,
  borderWidth: PropTypes.number,
  backgroundColor: PropTypes.string,
  padding: PropTypes.number,

  //Action handlers
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  onPointCreate: PropTypes.func,
  onPointChange: PropTypes.func,
  onPointDelete: PropTypes.func,
};

export default React.memo(ChartFieldLine);
